// ────────────────────────────────────────────────
// Диагностика устройств — типы данных (/diagnostics/*)
// ────────────────────────────────────────────────

import type { TicketCompany, TicketUser } from './ticketInterfaces';

/* ── Справочники ── */
export interface DeviceModel {
  id: number;
  name: string;
  category: string;
  item_guid: string | null;
  is_active: boolean;
  created_at?: string;
}

export interface Housing {
  id: number;
  name: string;
  is_active: boolean;
}

export interface FaultReason {
  id: number;
  text: string;
  usage_count: number;
  is_active: boolean;
}

/* ── Диагностические типы тикетов ── */
export type DiagnosticKind = 'onsite' | 'remote';

export interface DiagnosticTypeInfo {
  ticket_type: { id: number; name: string };
  kind: DiagnosticKind;
  notify_client_default: boolean;
}

/* ── Перечисления результата ── */
export type WarrantyStatus = 'unknown' | 'warranty' | 'non_warranty' | 'expired';
export type Condition = 'unknown' | 'ok' | 'faulty';
export type Disposition = 'none' | 'repair' | 'replace' | 'return' | 'write_off';
export type ResponsibleParty = 'none' | 'ta' | 'buyer' | 'kb' | 'harness_mfr';
export type DefectType = 'none' | 'production' | 'non_production';

export const WARRANTY_LABELS: Record<WarrantyStatus, string> = {
  unknown: 'Не определён',
  warranty: 'Гарантийный',
  non_warranty: 'Негарантийный',
  expired: 'Срок гарантии истёк',
};
export const CONDITION_LABELS: Record<Condition, string> = {
  unknown: 'Не определено',
  ok: 'Исправен',
  faulty: 'Неисправен',
};
export const DISPOSITION_LABELS: Record<Disposition, string> = {
  none: '—',
  repair: 'Ремонт',
  replace: 'Замена',
  return: 'Возврат',
  write_off: 'Списание',
};
export const RESPONSIBLE_LABELS: Record<ResponsibleParty, string> = {
  none: '—',
  ta: 'ТА',
  buyer: 'Покупатель',
  kb: 'КБ',
  harness_mfr: 'Изготовитель жгутов',
};
export const DEFECT_LABELS: Record<DefectType, string> = {
  none: '—',
  production: 'Производственный',
  non_production: 'Непроизводственный',
};

/* ── Устройство в тикете (строка) ── */
export interface DiagnosticDevice {
  id: number;
  ticket: number;
  model: number | null;
  custom_model_name: string | null;
  model_display: string;
  serial_number: string | null;
  housing: number | null;
  manufactured_at: string | null;
  received_at: string;
  client_reason: string;
  warranty_status: WarrantyStatus;
  condition: Condition;
  disposition: Disposition;
  fault_reason: number | null;
  fault_reason_text: string | null;
  reason_display: string;
  responsible_party: ResponsibleParty;
  defect_type: DefectType;
  engineer_note: string;
  created_at: string;
  updated_at: string;
}

/* Что заполняется при приёме (строка формы). Ровно одно из model/custom_model_name. */
export interface DeviceIntakePayload {
  model?: number | null;
  custom_model_name?: string | null;
  serial_number?: string | null;
  client_reason?: string;
  fault_reason?: number | null;
  fault_reason_text?: string | null;
  housing?: number | null;
  manufactured_at?: string | null;
  received_at?: string;
}

/* Результат диагностики (PATCH устройства). */
export interface DeviceResultPayload {
  warranty_status?: WarrantyStatus;
  condition?: Condition;
  disposition?: Disposition;
  fault_reason?: number | null;
  fault_reason_text?: string | null;
  responsible_party?: ResponsibleParty;
  defect_type?: DefectType;
  housing?: number | null;
  manufactured_at?: string | null;
  engineer_note?: string;
}

/* ── Тикет диагностики (список/деталь из /diagnostics/tickets/) ── */
export interface DiagnosticTicket {
  id: number;
  number: string;
  subject: string;
  status: string;
  ticket_type: { id: number; name: string } | null;
  company: TicketCompany | null;
  assigned_to: Pick<TicketUser, 'id' | 'username'> | null;
  sender_email?: string;
  notify_client: boolean | null;
  devices: DiagnosticDevice[];
  created_at: string;
}

export interface DiagnosticTicketCreatePayload {
  subject: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  ticket_type: number;
  company?: number | null;
  sender_email?: string;
  devices: DeviceIntakePayload[];
}

export interface DiagnosticCase {
  ticket: number;
  notify_client: boolean;
}

/* ── Дедуп по серийнику ── */
export interface SerialDuplicate {
  device_id: number;
  ticket_id: number;
  ticket_number: string | null;
  ticket_type: string | null;
  company: string | null;
  product: string;
  received_at: string;
}
export interface SerialLookupResponse {
  duplicates: SerialDuplicate[];
  count: number;
}

/* ── Отчёт ── */
export interface DiagnosticReportRow {
  id: number;
  ticket_id: number;
  ticket_number: string | null;
  ticket_type: string | null;
  product: string;
  serial: string | null;
  received_at: string;
  manufactured_at: string | null;
  client: string;
  housing_name: string | null;
  warranty_status: WarrantyStatus;
  condition: Condition;
  disposition: Disposition;
  reason: string;
  responsible_party: ResponsibleParty;
  defect_type: DefectType;
  engineer_note: string;
}

export interface DiagnosticReportFilters {
  received_from?: string;
  received_to?: string;
  company?: number | '';
  ticket_type?: number | '';
  model?: number[];
  warranty_status?: WarrantyStatus | '';
  condition?: Condition | '';
  disposition?: Disposition | '';
  responsible_party?: ResponsibleParty | '';
  defect_type?: DefectType | '';
  housing?: number | '';
  assignee?: number | '';
  serial?: string;
}

export interface DiagnosticReportSummary {
  total: number;
  by_model: { model_name: string | null; n: number }[];
  by_defect_type: { defect_type: DefectType; n: number }[];
  by_responsible_party: { responsible_party: ResponsibleParty; n: number }[];
  by_warranty: { warranty_status: WarrantyStatus; n: number }[];
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/* ── Жизненный цикл: допустимые следующие статусы ── */
export const DIAG_STATUS_FLOW: Record<DiagnosticKind, Record<string, string[]>> = {
  onsite: {
    diag_new: ['diag_received'],
    diag_received: ['diag_in_progress'],
    diag_in_progress: ['diag_waiting_client', 'diag_diagnosed'],
    diag_waiting_client: ['diag_in_progress'],
    diag_diagnosed: ['diag_closed', 'diag_in_progress'],
    diag_closed: [],
  },
  remote: {
    remote_new: ['remote_in_progress'],
    remote_in_progress: ['remote_diagnosed'],
    remote_diagnosed: ['remote_closed', 'remote_in_progress'],
    remote_closed: [],
  },
};

export const DIAG_FINAL_STATUSES = new Set(['diag_closed', 'remote_closed']);
